import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Calendar, CheckCircle2, XCircle, DollarSign, Clock, AlertTriangle,
  FileText, Plus, ChevronRight, PartyPopper, ClipboardList,
} from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

const PlannerHoy = () => {
  const [data, setData] = useState(null);
  const [outbox, setOutbox] = useState({ items: [], twilio_activo: false });
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: nb }] = await Promise.all([
        axios.get(`${API}/agenda/hoy`),
        axios.get(`${API}/notificaciones/outbox`).catch(() => ({ data: { items: [], twilio_activo: false } })),
      ]);
      setData(d);
      setOutbox(nb);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar el día");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const aprobar = async (pago_id) => {
    setApproving(pago_id);
    try {
      await axios.post(`${API}/pagos/${pago_id}/aprobar`);
      toast.success("Pago aprobado · evento confirmado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aprobar");
    } finally { setApproving(null); }
  };

  const rechazar = async (pago_id) => {
    const motivo = window.prompt("¿Motivo del rechazo? (opcional)") || "";
    setApproving(pago_id);
    try {
      await axios.post(`${API}/pagos/${pago_id}/rechazar`, { motivo });
      toast.success("Pago rechazado y evento liberado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo rechazar");
    } finally { setApproving(null); }
  };

  if (loading || !data) {
    return <div className="p-6 text-stone-500 text-sm">Cargando tu día...</div>;
  }

  const r = data.resumen;
  const hoy = new Date(data.fecha + "T00:00:00").toLocaleDateString("es-MX", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="space-y-6" data-testid="planner-hoy">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Planner del día</p>
          <h1 className="font-display text-3xl sm:text-4xl text-stone-900 capitalize">{hoy}</h1>
          <p className="text-stone-500 text-sm mt-1">Todo lo importante en una sola pantalla.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/panel/nuevo-evento")} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-nuevo-evento">
            <Plus className="w-4 h-4 mr-2" /> Nuevo evento
          </Button>
        </div>
      </div>

      {/* 4 tarjetas accionables */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ActionCard
          tone="amber"
          icon={AlertTriangle}
          label="Por aprobar"
          value={r.pagos_por_aprobar}
          sub={fmt(r.monto_por_aprobar)}
          cta={r.pagos_por_aprobar > 0 ? "Revisar →" : null}
          onClick={() => document.getElementById("pagos-pendientes")?.scrollIntoView({ behavior: "smooth" })}
          testid="card-pagos-aprobar"
        />
        <ActionCard
          tone="rose"
          icon={DollarSign}
          label="Cobros hoy"
          value={r.cobros_hoy}
          sub={fmt(r.monto_cobros_hoy)}
          cta={r.cobros_hoy > 0 ? "Ver contratos →" : null}
          onClick={() => navigate("/panel/contratos")}
          testid="card-cobros-hoy"
        />
        <ActionCard
          tone="emerald"
          icon={PartyPopper}
          label="Eventos hoy"
          value={r.eventos_hoy}
          sub={`${r.eventos_semana} esta semana`}
          cta="Ver agenda →"
          onClick={() => navigate("/panel/agenda")}
          testid="card-eventos-hoy"
        />
        <ActionCard
          tone="violet"
          icon={ClipboardList}
          label="Por hacer"
          value={r.tareas_vencidas + r.cotizaciones_abiertas}
          sub={`${r.tareas_vencidas} vencidas · ${r.cotizaciones_abiertas} cotiz.`}
          cta="Ver pipeline →"
          onClick={() => navigate("/panel/pipeline")}
          testid="card-tareas"
        />
      </div>

      {/* Notificaciones por enviar (WhatsApp) */}
      {outbox.items.length > 0 && (
        <Card className="border-emerald-300 bg-emerald-50/40" data-testid="outbox-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-display text-xl text-stone-900 flex items-center gap-2">
                  💬 Notificaciones por enviar
                </h2>
                <p className="text-xs text-stone-500">
                  {outbox.twilio_activo
                    ? "Envío automático activo · estas son errores que requieren tu atención"
                    : "Pulsa Enviar en cada una para abrir WhatsApp con el mensaje listo. (Activa Twilio para envío 100% automático)"}
                </p>
              </div>
              <span className="bg-emerald-200 text-emerald-900 text-xs font-medium px-2 py-1 rounded">{outbox.items.length}</span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {outbox.items.slice(0, 8).map(n => (
                <div key={n.notif_id} className="bg-white border border-emerald-200 rounded-lg p-3 flex items-start gap-3" data-testid={`notif-${n.notif_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-stone-500">{n.tipo.replace(/_/g, " ")} → {n.destino_nombre || n.destino_telefono}</p>
                    <p className="text-sm text-stone-800 truncate">{(n.mensaje || "").split("\n")[0]}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {n.wa_link && (
                      <a href={n.wa_link} target="_blank" rel="noreferrer" onClick={async () => { await axios.post(`${API}/notificaciones/${n.notif_id}/marcar-enviada`); cargar(); }} className="px-3 py-1 bg-emerald-700 text-white rounded text-xs hover:bg-emerald-800" data-testid={`enviar-${n.notif_id}`}>
                        Enviar
                      </a>
                    )}
                    <button onClick={async () => { await axios.post(`${API}/notificaciones/${n.notif_id}/descartar`); cargar(); }} className="px-2 py-1 border border-stone-300 rounded text-xs text-stone-600 hover:bg-stone-50" data-testid={`descartar-${n.notif_id}`}>
                      Descartar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagos pendientes de aprobación — bloque destacado */}
      <Card id="pagos-pendientes" className="border-amber-300 bg-amber-50/30">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display text-xl text-stone-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-700" /> Pagos por aprobar
              </h2>
              <p className="text-xs text-stone-500">Los pagos del checkout quedan aquí hasta que tú los valides.</p>
            </div>
            {r.pagos_por_aprobar > 0 && (
              <span className="bg-amber-200 text-amber-900 text-xs font-medium px-2 py-1 rounded">
                {r.pagos_por_aprobar} pendiente{r.pagos_por_aprobar > 1 ? "s" : ""}
              </span>
            )}
          </div>
          {data.pagos_por_aprobar.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              <p className="text-stone-600 text-sm">Todo al día. No hay pagos pendientes de aprobación.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.pagos_por_aprobar.map(p => (
                <div key={p.pago_id} className="bg-white border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`pago-pendiente-${p.pago_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900">{p.evento?.nombre || p.evento?.tipo || "Evento"}</p>
                    <p className="text-xs text-stone-600 truncate">
                      {p.cliente?.nombre || "Cliente"} · {p.cliente?.whatsapp || p.cliente?.telefono || p.cliente?.email || "sin contacto"}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      Fecha evento: {p.evento?.fecha || p.evento?.fecha_evento || "—"} · {p.metodo} · ref: {p.referencia || "—"}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display text-xl text-stone-900">{fmt(p.monto_pagado)}</p>
                    <p className="text-[10px] text-stone-500">de {fmt(p.monto_total)} totales</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => rechazar(p.pago_id)} disabled={approving === p.pago_id} data-testid={`btn-rechazar-${p.pago_id}`}>
                      <XCircle className="w-4 h-4 text-red-600 mr-1" /> Rechazar
                    </Button>
                    <Button size="sm" onClick={() => aprobar(p.pago_id)} disabled={approving === p.pago_id} className="bg-emerald-900 hover:bg-emerald-800" data-testid={`btn-aprobar-${p.pago_id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bloques inferiores */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Eventos de hoy */}
        <Card className="border-stone-200">
          <CardContent className="p-5">
            <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
              <PartyPopper className="w-4 h-4 text-emerald-700" /> Eventos hoy
            </h3>
            {data.eventos_hoy.length === 0 ? (
              <p className="text-stone-500 text-sm">Sin eventos programados hoy.</p>
            ) : (
              <div className="space-y-2">
                {data.eventos_hoy.slice(0, 5).map(ev => (
                  <Link key={ev.evento_id} to={`/panel/eventos/${ev.evento_id}`} className="block border border-stone-200 rounded p-2 hover:bg-stone-50">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm text-stone-900">{ev.nombre || ev.tipo}</p>
                      <ChevronRight className="w-4 h-4 text-stone-400" />
                    </div>
                    <p className="text-xs text-stone-500">{ev.hora_inicio || "—"} · {ev.ubicacion || "—"} · {(ev.unidades || []).join(", ") || "—"}</p>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cotizaciones abiertas */}
        <Card className="border-stone-200">
          <CardContent className="p-5">
            <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-stone-700" /> Cotizaciones abiertas
            </h3>
            {data.cotizaciones_abiertas.length === 0 ? (
              <p className="text-stone-500 text-sm">Sin cotizaciones pendientes.</p>
            ) : (
              <div className="space-y-2">
                {data.cotizaciones_abiertas.slice(0, 5).map(c => (
                  <Link key={c.cotizacion_id} to={`/panel/pipeline`} className="block border border-stone-200 rounded p-2 hover:bg-stone-50">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-stone-900">Cotización {c.cotizacion_id?.slice(-6)}</p>
                      <span className="text-[10px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded">{c.estatus}</span>
                    </div>
                    <p className="text-xs text-stone-500">{fmt(c.total)}</p>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Próximos 7 días */}
      <Card className="border-stone-200">
        <CardContent className="p-5">
          <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-stone-700" /> Próximos 7 días
          </h3>
          {data.eventos_semana.length === 0 ? (
            <p className="text-stone-500 text-sm">Sin eventos en los próximos 7 días.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.eventos_semana.slice(0, 9).map(ev => (
                <Link key={ev.evento_id} to={`/panel/eventos/${ev.evento_id}`} className="border border-stone-200 rounded p-2 hover:bg-stone-50">
                  <p className="text-xs text-stone-500">{ev.fecha || ev.fecha_evento}</p>
                  <p className="font-medium text-sm text-stone-900 truncate">{ev.nombre || ev.tipo}</p>
                  <p className="text-xs text-stone-500">{(ev.unidades || []).join(", ") || "—"}</p>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const TONE = {
  amber: "border-amber-300 bg-amber-50",
  rose: "border-rose-300 bg-rose-50",
  emerald: "border-emerald-300 bg-emerald-50",
  violet: "border-violet-300 bg-violet-50",
};
const TEXT = {
  amber: "text-amber-800",
  rose: "text-rose-800",
  emerald: "text-emerald-800",
  violet: "text-violet-800",
};

const ActionCard = ({ tone, icon: Icon, label, value, sub, cta, onClick, testid }) => (
  <button onClick={onClick} className={`text-left rounded-xl border p-4 transition hover:shadow-md ${TONE[tone]}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] uppercase tracking-wider text-stone-600">{label}</p>
      <Icon className={`w-4 h-4 ${TEXT[tone]}`} />
    </div>
    <p className={`font-display text-3xl ${TEXT[tone]}`}>{value}</p>
    <p className="text-xs text-stone-600 mt-1">{sub}</p>
    {cta && <p className={`text-xs font-medium mt-2 ${TEXT[tone]}`}>{cta}</p>}
  </button>
);

export default PlannerHoy;
